import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { api, apiRequest } from "@/lib/queryClient";
import { authKeys } from "@/constant/queryKeys";
import { User } from "@/hooks/useAuthStore";
import type { OuraAdminStatus } from "@/types/oura";

export interface AuthMeResponse {
  user: User;
}

export interface AuthLoginRequest {
  code: string;
  code_verifier?: string;
  redirect_uri: string;
}

export interface AuthLoginResponse {
  user: {
    id: number;
    name: string;
    username: string;
    profile_image_url: string;
  };
}

export interface LinkedAccountsResponse {
  x: { isPrimary: boolean; email?: string; linkedAt: string } | null;
  google: { isPrimary: boolean; email?: string; linkedAt: string } | null;
}

export function getAuthMeOptions() {
  return queryOptions({
    queryKey: authKeys.me,
    queryFn: () => apiRequest<AuthMeResponse>("GET", "/api/v1/auth/me"),
  });
}

export function getAuthMeQuietOptions() {
  return queryOptions({
    queryKey: authKeys.me,
    queryFn: async () => {
      const response = await api.get<AuthMeResponse>("/api/v1/auth/me", {
        skipUnauthorizedHandler: true,
      } as any);

      return response.data;
    },
  });
}

export function getAdminStatusOptions() {
  return queryOptions({
    queryKey: authKeys.adminStatus,
    queryFn: () => apiRequest<OuraAdminStatus>("GET", "/api/v1/auth/admin-status"),
    retry: false,
  });
}

export function getLinkedAccountsOptions() {
  return queryOptions({
    queryKey: authKeys.linkedAccounts,
    queryFn: () => apiRequest<LinkedAccountsResponse>("GET", "/api/v1/auth/linked-accounts"),
  });
}

export function postAuthLoginOptions(provider: 'x' | 'google', force?: boolean) {
  return mutationOptions<AuthLoginResponse, Error, AuthLoginRequest>({
    mutationKey: authKeys.login(provider, force),
    mutationFn: (data: AuthLoginRequest) => {
      const endpoint = force 
        ? `/api/v1/auth/${provider}/login?force=true` 
        : `/api/v1/auth/${provider}/login`;
      return apiRequest<AuthLoginResponse>("POST", endpoint, data);
    },
  });
}

export function postAuthLinkOptions(provider: 'x' | 'google') {
  return mutationOptions<any, Error, AuthLoginRequest>({
    mutationKey: authKeys.link(provider),
    mutationFn: (data: AuthLoginRequest) => apiRequest("POST", `/api/v1/auth/${provider}/link`, data),
  });
}

export function deleteUnlinkProviderOptions() {
  return mutationOptions({
    mutationKey: authKeys.unlink,
    mutationFn: (provider: 'x' | 'google') => apiRequest("DELETE", `/api/v1/auth/unlink/${provider}`),
  });
}

export function postAuthLogoutOptions() {
  return mutationOptions({
    mutationKey: authKeys.logout,
    mutationFn: () => apiRequest("POST", "/api/v1/auth/logout"),
  });
}
